import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Platform } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { initializeApp, getApps } from 'firebase/app';
import { getAnalytics, logEvent as firebaseLogEvent } from 'firebase/analytics';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analyticsInitialized = false;
  private isMobile = false;
  private analytics: any = null;

  constructor(
    private router: Router,
    private platform: Platform
  ) {
    this.isMobile = this.platform.is('capacitor') || this.platform.is('cordova') || this.platform.is('hybrid');
  }

  async initialize(): Promise<void> {
    if (!environment.enableAnalytics) {
      return;
    }

    try {

      // Initialiser Firebase si ce n'est pas déjà fait
      const apps = getApps();
      let app;

      if (apps.length === 0) {
        app = initializeApp(environment.firebase);
      } else {
        app = apps[0];
      }

      // Initialiser Analytics
      this.analytics = getAnalytics(app);
      this.analyticsInitialized = true;

      // Activer le mode debug en développement pour web
      if (environment.debugAnalytics && !this.isMobile) {
        this.enableDebugMode();
      }

      this.trackPageViews();
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation Analytics:', error);
      this.analyticsInitialized = false;
    }
  }

  private enableDebugMode(): void {
    // Activer le mode debug via gtag (sous-jacent de Firebase Analytics)
    try {
      // @ts-ignore
      if (typeof window !== 'undefined' && window.gtag) {
        // @ts-ignore
        window.gtag('config', environment.firebase.measurementId, {
          'debug_mode': true
        });
      } else {
        console.warn('⚠️ gtag non disponible, utilisation de l\'extension Google Analytics Debugger recommandée');
      }
    } catch (error) {
      console.warn('⚠️ Impossible d\'activer le mode debug via gtag:', error);
    }
  }

  private trackPageViews(): void {

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      if (this.analyticsInitialized) {
        this.logPageView(event.urlAfterRedirects);
      }
    });
  }

  private async logPageView(pageUrl: string): Promise<void> {
    if (!this.analyticsInitialized) {
      console.warn('⚠️ Analytics non initialisé, impossible de logger la page view');
      return;
    }

    try {
      const pageTitle = this.extractPageTitle(pageUrl);
      const platform = this.isMobile ? 'mobile' : 'web';

      firebaseLogEvent(this.analytics, 'page_view' as any, {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pageUrl,
        platform: platform
      });

    } catch (error) {
      console.error('❌ Erreur lors du log page view:', error);
    }
  }

  private extractPageTitle(url: string): string {
    if (!url) return 'Unknown';
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter(s => s.length > 0);
    const lastSegment = segments[segments.length - 1];
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
